import {AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChildren} from '@angular/core';
import {RegisterCompanyStepBase} from '../register-company-step.base';

@Component({
    selector: 'app-register-company-otp',
    templateUrl: './otp-step.component.html',
    styleUrls: ['../../register-company.component.scss']
})
export class RegisterCompanyOtpStepComponent extends RegisterCompanyStepBase implements AfterViewInit {

    @Input() OTPCode = '';
    @Input() showResendOtpButton = false;
    @Input() showOtpResendCountdown = false;
    @Input() otpResendSecondsLeft = 0;
    @Input() isResendingOtp = false;

    @Output() OTPCodeChange = new EventEmitter<string>();
    @Output() verify = new EventEmitter<{ otp: string; email: string }>();
    @Output() resend = new EventEmitter<void>();

    @ViewChildren('otpSlot') otpSlots!: QueryList<ElementRef<HTMLInputElement>>;

    readonly otpIndexes = [0, 1, 2, 3, 4, 5];
    submitted = false;

    ngAfterViewInit(): void {
        setTimeout(() => this.focusSlot(0));
    }

    get isOtpComplete(): boolean {
        return this.OTPCode.length === 6;
    }

    otpCharAt(index: number): string {
        return this.OTPCode[index] ?? '';
    }

    onAutofillInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.applyFullCode(input.value ?? '');
        input.value = '';
    }

    onBeforeInput(index: number, event: InputEvent): void {
        const inserted = (event.data ?? '').replace(/[^a-zA-Z0-9]/g, '');
        if (inserted.length > 1) {
            event.preventDefault();
            this.applyFullCode(inserted);
        }
    }

    onSlotInput(index: number, event: Event): void {
        const input = event.target as HTMLInputElement;
        const sanitized = (input.value ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

        if (sanitized.length > 1) {
            this.applyFullCode(sanitized);
            return;
        }

        const char = sanitized.slice(-1);
        this.setOtpChar(index, char);
        input.value = char;
        if (char) {
            this.focusSlot(index + 1);
        }
        if (this.isOtpComplete) {
            this.checkOTP();
        }
    }

    onSlotKeydown(index: number, event: KeyboardEvent): void {
        if (event.key === 'Backspace') {
            event.preventDefault();
            if (this.otpCharAt(index)) {
                this.setOtpChar(index, '');
                this.focusSlot(index);
            } else {
                this.setOtpChar(index - 1, '');
                this.focusSlot(index - 1);
            }
            return;
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.focusSlot(index - 1);
            return;
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.focusSlot(index + 1);
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            this.checkOTP();
        }
    }

    onOtpPaste(event: ClipboardEvent): void {
        event.preventDefault();
        this.applyFullCode(event.clipboardData?.getData('text') ?? '');
    }

    onSlotFocus(event: FocusEvent): void {
        (event.target as HTMLInputElement).select();
    }

    checkOTP(): void {
        this.submitted = true;
        if (!this.isOtpComplete || this.isProcessing) return;
        this.verify.emit({otp: this.OTPCode, email: this.company.legalEmail});
    }

    resendOtp(): void {
        this.resend.emit();
    }

    private applyFullCode(raw: string): void {
        const code = (raw ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6);
        if (!code) return;
        this.setOtpValue(code);
        this.syncSlotValues();
        this.focusSlot(Math.min(Math.max(code.length - 1, 0), 5));
        if (code.length === 6) {
            this.checkOTP();
        }
    }

    private syncSlotValues(): void {
        this.otpSlots?.forEach((slot, index) => {
            slot.nativeElement.value = this.otpCharAt(index);
        });
    }

    private setOtpChar(index: number, char: string): void {
        if (index < 0 || index > 5) return;
        const chars = this.otpIndexes.map(i => this.otpCharAt(i));
        chars[index] = char;
        this.setOtpValue(chars.join('').replace(/[^A-Z0-9]/g, '').slice(0, 6));
    }

    private setOtpValue(value: string): void {
        this.OTPCode = value;
        this.OTPCodeChange.emit(this.OTPCode);
    }

    private focusSlot(index: number): void {
        const slots = this.otpSlots?.toArray() ?? [];
        if (!slots.length) return;
        const target = slots[Math.max(0, Math.min(index, slots.length - 1))];
        target?.nativeElement.focus();
        target?.nativeElement.select();
    }
}
